import {
  FloatingArrow,
  FloatingPortal,
  type Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import {type TooltipBaseProps, cn} from '@tempmee/core'
import {cloneElement, isValidElement, useRef, useState} from 'react'
import {TooltipVariantsFactory} from './Variants'

/** Placement of the tooltip relative to its trigger (floating-ui placements). */
export type TooltipPlacement = Placement

export type {TooltipSize} from '@tempmee/core'

export interface TooltipProps extends TooltipBaseProps {
  /** Preferred placement of the tooltip (auto-flips to stay in view). @default 'top' */
  placement?: TooltipPlacement
  /** Delay in ms before showing the tooltip on hover (MUI `enterDelay`). @default 200 */
  enterDelay?: number
  /**
   * Delay in ms before showing the tooltip on hover.
   * @deprecated Use `enterDelay` (MUI-aligned).
   */
  delay?: number
}

/**
 * Tooltip — informative text shown on hover, focus, or tap (MUI `Tooltip` contract).
 *
 * v3 re-platform: dark-teal bubble with a small label and an optional directional
 * arrow. Positioned + portaled via floating-ui. The legacy `content`/`delay` props
 * still work (deprecated) — prefer `title`/`enterDelay`.
 *
 * @see https://mui.com/material-ui/react-tooltip
 */
export const Tooltip = ({
  title,
  content,
  children,
  placement = 'top',
  arrow: showArrow = true,
  size = 'small',
  enterDelay,
  delay,
  disabled = false,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  // MUI-aligned props win; fall back to the deprecated aliases.
  const label = title ?? content
  const openDelay = enterDelay ?? delay ?? 200

  const {refs, floatingStyles, context} = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(showArrow ? 8 : 6),
      flip(),
      shift({padding: 8}),
      arrow({element: arrowRef}),
    ],
  })

  const hover = useHover(context, {
    delay: {open: openDelay, close: 0},
    enabled: !disabled,
  })
  const focus = useFocus(context, {enabled: !disabled})
  const dismiss = useDismiss(context)
  const role = useRole(context, {role: 'tooltip'})

  const {getReferenceProps, getFloatingProps} = useInteractions([hover, focus, dismiss, role])

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {
    duration: 150,
  })

  const triggerProps = getReferenceProps({ref: refs.setReference})

  return (
    <>
      {isValidElement(children) && cloneElement(children, triggerProps)}
      {isMounted && label != null && label !== '' && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{...floatingStyles, ...transitionStyles}}
            className={cn(TooltipVariantsFactory({size}))}
            {...getFloatingProps()}
          >
            {label}
            {showArrow && (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                className="fill-primary-contrast"
                width={12}
                height={6}
              />
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Tooltip.displayName = 'Tooltip'
